import {ChangeEventHandler, KeyboardEventHandler, MouseEventHandler, useState} from 'react';
import {styled, useTheme} from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Fade from '@mui/material/Fade';

import {useIsDownSm} from 'utils/hooks/useMediaQuery';
import {shouldNotForwardProp} from 'utils/styled';

import CrossIcon from 'components/icons/cross.svg';
import DropdownIcon from 'components/icons/dropdown.svg';
import TextField, {TextFieldProps} from 'components/form-next/TextField';
import {useTreeSearchProps} from 'components/Tree/utils/useTreeSearch';
import type {TreeBasicNode} from 'components/Tree/TreeTypes';
import {TreeKey} from 'components/Tree/TreeTypes';
import LoadingIndicator from 'components/LoadingIndicator';

import {TreePickerBox, TreePickerListBoxProps} from './TreePickerBox';

export type TreePickerAutocompleteProps<K extends TreeKey, T extends TreeBasicNode<K>> = {
  entityName: string;
  handlePopupToggle?: (open: boolean) => void;
} & Pick<
  TreePickerListBoxProps<K, T>,
  | 'treeData'
  | 'selected'
  | 'setSelected'
  | 'selectionCount'
  | 'loading'
  | 'disabledKeys'
  | 'searchKeys'
  | 'rightColRenderer'
  | 'getLocalSelectedLabel'
> &
  Pick<TextFieldProps, 'error' | 'helperText' | 'helperTextRequired' | 'disabled'> &
  DataTestId;

const SRoot = styled('div')(() => ({
  width: '100%',
}));

const SInputAdornment = styled(InputAdornment)(({theme}) => ({
  marginRight: theme.spacing(-1.5),
}));

const SClearButtonDummy = styled('div')({
  width: 1,
});

const SCrossIcon = styled(
  CrossIcon,
  shouldNotForwardProp('hasFocus')
)<{
  hasFocus: boolean;
}>(({theme, hasFocus}) => ({
  color: hasFocus ? theme.palette.text.primary : theme.palette.text.secondary,
}));

const SIconButton = styled(IconButton)(({theme}) => ({
  padding: theme.spacing(0.5),
}));

const SDropdownIcon = styled(
  DropdownIcon,
  shouldNotForwardProp('open', 'hasFocus')
)<{
  open: boolean;
  hasFocus: boolean;
}>(({theme, open, hasFocus}) => ({
  transition: theme.transitions.create(['transform'], {
    duration: theme.transitions.duration.shortest,
  }),
  transform: open ? 'rotate(180deg)' : 'unset',
  color: hasFocus ? theme.palette.text.primary : theme.palette.text.secondary,
}));

const SPaper = styled('div')(({theme}) => ({
  backgroundColor: theme.palette.common.white,
  borderRadius: theme.shape.borderRadiusRegular,
  border: `1px solid ${theme.palette.divider}`,
}));

export const extraCaseForMarketsSelectionCount = (cCount: number | string, iCount: number | string) => {
  if ([cCount, iCount].every(count => count === 0 || count === 'All')) {
    return 'All';
  }

  return `C: ${cCount}, I: ${iCount}`;
};

export function TreePickerAutocomplete<K extends TreeKey, T extends TreeBasicNode<K>>({
  loading,
  treeData,
  entityName,
  selected = [],
  setSelected,
  handlePopupToggle,
  selectionCount,
  disabledKeys,
  error,
  helperText,
  helperTextRequired,
  dataTestId,
  searchKeys,
  disabled,
  rightColRenderer,
  getLocalSelectedLabel,
}: TreePickerAutocompleteProps<K, T>) {
  const isDownSm = useIsDownSm();
  const theme = useTheme();
  const [hasFocus, setHasFocus] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [localSelected, setLocalSelected] = useState<K[]>(selected);
  // we can't check weather `selectionCount` is a number because for markets it always a string
  const selectionIdled =
    String(selectionCount) === '0' ||
    selectionCount === 'All' ||
    selectionCount === extraCaseForMarketsSelectionCount(0, 0) ||
    selectionCount === extraCaseForMarketsSelectionCount('All', 'All');

  const {textFieldProps, searchProps, treeProps, isNothingFound, filteredTree} = useTreeSearchProps<K, T>({
    treeData,
    searchKeys,
  });

  const handleOpen = () => {
    if (open) {
      return;
    }

    setLocalSelected(selected);
    setOpen(true);
    handlePopupToggle?.(true);
  };

  const handleClose = () => {
    if (!open) {
      return;
    }

    searchProps.onChange(undefined, '');
    setOpen(false);
    handlePopupToggle?.(false);
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = event => {
    const newValue = event.target.value;

    textFieldProps.onChange(event);

    if (newValue !== '') {
      handleOpen();
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Escape' && open) {
      // Avoid the Modal to handle the event.
      e.stopPropagation();
      handleClose();
    }
  };

  const handleClear = () => {
    searchProps.onChange(undefined, '');
  };

  const handlePopupIndicator: MouseEventHandler<HTMLButtonElement> = (/* event */) => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  const onFocus = () => {
    setHasFocus(true);
    handleOpen();
  };

  return (
    <ClickAwayListener mouseEvent='onMouseDown' touchEvent='onTouchStart' onClickAway={handleClose}>
      <SRoot>
        <TextField
          data-testid={dataTestId}
          fullWidth
          color='secondary'
          label={entityName}
          value={textFieldProps.value || (hasFocus || selectionIdled ? textFieldProps.value : selectionCount)}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          onBlur={() => setHasFocus(false)}
          slotProps={{
            input: {
              ref: setAnchorEl,
              endAdornment: (
                <SInputAdornment position='end'>
                  {textFieldProps.value.length ? (
                    <IconButton size='small' onClick={handleClear}>
                      <SCrossIcon hasFocus={hasFocus} />
                    </IconButton>
                  ) : (
                    <SClearButtonDummy />
                  )}

                  {loading ? (
                    <LoadingIndicator size={16} padding={0} />
                  ) : (
                    <SIconButton
                      title={open ? 'Close' : 'Open'}
                      disabled={disabled}
                      size='small'
                      onClick={handlePopupIndicator}
                      data-testid='tree-picker-autocomplete-drop-down-button'
                    >
                      <SDropdownIcon open={open} hasFocus={hasFocus} />
                    </SIconButton>
                  )}
                </SInputAdornment>
              ),
            },
            htmlInput: {
              autoComplete: 'off',
              autoCapitalize: 'none',
              spellCheck: 'false',
              placeholder: hasFocus ? 'Select' : undefined,
            },
          }}
          error={error}
          helperText={helperText}
          helperTextRequired={helperTextRequired}
          disabled={disabled}
        />

        <Popper
          transition
          placement='auto-start'
          open={open}
          anchorEl={anchorEl}
          modifiers={[
            {
              name: 'offset',
              options: {
                offset: () => [0, 4],
              },
            },
            {
              name: 'flip',
              options: {
                allowedAutoPlacements: ['bottom-start', 'top-start'],
              },
            },
          ]}
          style={{
            width: isDownSm ? '100vw' : `calc(600px - ${theme.spacing(8)})`,
            zIndex: theme.zIndex.modal + 1000,
          }}
        >
          {({TransitionProps}) => (
            <Fade {...TransitionProps}>
              <SPaper>
                <div data-testid='tree-picker-autocomplete-box'>
                  <TreePickerBox
                    entityTitle={entityName}
                    loading={loading}
                    searching={(textFieldProps.value || '').length !== 0}
                    setSelected={setSelected}
                    localSelected={localSelected}
                    setLocalSelected={setLocalSelected}
                    selectionCount={selectionCount}
                    isNothingFound={isNothingFound}
                    disabledKeys={disabledKeys}
                    handleClose={handleClose}
                    clearSearch={handleClear}
                    rightColRenderer={rightColRenderer}
                    getLocalSelectedLabel={getLocalSelectedLabel}
                    treeData={searchProps.value ? filteredTree : treeData}
                    fullTreeData={treeData}
                    {...treeProps}
                  />
                </div>
              </SPaper>
            </Fade>
          )}
        </Popper>
      </SRoot>
    </ClickAwayListener>
  );
}
